import React from 'react';
import { parseApiUtc } from '@/utils/apiDate';
import { appDateTime } from '@/utils/appDateFormat';
import { useTranslation } from 'react-i18next';
import { InfoTooltip } from '@/components/ui/Tooltip';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/Accordion';
import { humanizeFieldLabel } from '@/utils/humanizeFieldLabel';

/**
 * The clarity breakdown for a business role or a goal.
 *
 * It lives here rather than inside either edit form because a clarity score is a *property of the
 * saved record*, not a control on a form. The server recalculates it on every save, so a meter
 * rendered inside the form was showing a preview that the save could then disagree with — two
 * numbers for one thing. Read it where you read the record.
 *
 * The score itself is always on screen; the reasoning behind it is one click away. They answer two
 * different questions — "how clear is this, and is that number current?" is the glance, "which part
 * is dragging it down?" is the follow-up — and only the first belongs in a details panel a reader is
 * scrolling past. The disclosure is the shared `Accordion` primitive rather than a hand-rolled
 * toggle, so keyboard support, focus ring and the open/close animation match the rest of the app.
 *
 * Roles and goals produce the same shape from different calculators, so both render through here
 * and cannot drift apart. The only thing that differs is where the per-field labels come from,
 * which is what `fieldLabelKey` is for.
 */

export interface ClarityFieldBreakdown {
  field: string;
  percent: number;
  note?: string;
}

export interface ClarityBreakdown {
  overallPercent: number;
  perField: ClarityFieldBreakdown[];
  recommendations: string[];
  isUnclear?: boolean;
  aiAvailable?: boolean;
  /** How the score was derived. Only goal clarity sets it; role clarity leaves it undefined. */
  scoreSource?: string;
}

interface ClarityMeterProps {
  clarity?: ClarityBreakdown | null;
  /** Falls back to the score alone when no breakdown was stored (e.g. a record saved before it existed). */
  score?: number | null;
  /** When the score was last recalculated. Shown so a stale number reads as stale. */
  updatedOn?: string | null;
  /** i18n key prefix for the per-field labels, e.g. `businessRoles.clarityField`. */
  fieldLabelKey: string;
  /** Sentence explaining what the bars measure. */
  howScoredKey: string;
  /** Sentence explaining where the score ends up. */
  feedsKey: string;
  headingKey: string;
  hintKey: string;
  emptyKey: string;
  'data-testid'?: string;
  /** Prefix for the inner test ids, so a role and a goal meter stay distinguishable. */
  testIdPrefix?: string;
}

// Green at 80, amber from 50, red below. Same bands as the list chips.
function tone(pct: number): { bar: string; text: string; track: string } {
  if (pct >= 80) return { bar: 'bg-success', text: 'text-success', track: 'bg-success/15' };
  if (pct >= 50) return { bar: 'bg-warning', text: 'text-warning', track: 'bg-warning/15' };
  return { bar: 'bg-destructive', text: 'text-destructive', track: 'bg-destructive/15' };
}

const ClarityMeter: React.FC<ClarityMeterProps> = ({
  clarity,
  score,
  updatedOn,
  fieldLabelKey,
  howScoredKey,
  feedsKey,
  headingKey,
  hintKey,
  emptyKey,
  'data-testid': testId = 'clarity-meter',
  testIdPrefix = 'clarity',
}) => {
  const { t, i18n } = useTranslation();

  const overall = clarity?.overallPercent ?? score ?? null;
  const perField = clarity?.perField ?? [];
  const recommendations = clarity?.recommendations ?? [];
  // Nothing to disclose means no trigger: an accordion that opens onto an empty panel is worse than
  // no accordion. An old record with a score but no stored breakdown lands here.
  const hasDetail = perField.length > 0 || recommendations.length > 0;

  // Capped: the model scored higher than the goal's structure supports, so it was held down.
  // Quarantined: the scored text tried to instruct the scorer, so the rubric scored it alone.
  const sourceNoteKey =
    clarity?.scoreSource === 'Capped' ? 'clarity.source.capped'
      : clarity?.scoreSource === 'Quarantined' ? 'clarity.source.quarantined'
        : null;

  return (
    <section className="rounded-lg border border-border" data-testid={testId}>
      <div className="p-4">
        <div>
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold text-foreground">{t(headingKey)}</h3>
            {/* Two things nobody could work out from the meter alone: what the per-field bars are
                measuring, and that this number ends up on the engagement dashboard. It sits
                outside the disclosure trigger below, because a tooltip trigger nested inside a
                button is a broken control for keyboard and screen-reader users. */}
            <InfoTooltip
              data-testid={`${testIdPrefix}-scoring-info`}
              label={t(headingKey) as string}
              content={
                <>
                  <span className="block">{t(howScoredKey)}</span>
                  <span className="mt-1 block text-muted-foreground">{t(feedsKey)}</span>
                </>
              }
            />
          </div>
          <p className="text-xs text-muted-foreground">{t(hintKey)}</p>
        </div>

        {overall == null ? (
          <p className="mt-3 text-sm text-muted-foreground" data-testid={`${testIdPrefix}-empty`}>
            {t(emptyKey)}
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {/* Qualifies the number itself, so it stays with the number rather than hiding behind
                the disclosure. */}
            {/* Why this number is what it is.
                A score that is HELD does not move when the user improves something the rubric does
                not weigh, and a frozen number with no explanation reads as a broken feature: this
                was reported as "I changed the skills and the score did not change". `Capped` and
                `Quarantined` are the two states where the model's answer was overridden, so they are
                the two worth explaining. `Ai` and `Fallback` need no note. */}
            {sourceNoteKey && (
              <p
                className="text-xs text-muted-foreground"
                data-testid={`${testIdPrefix}-source-note`}
              >
                {t(sourceNoteKey)}
              </p>
            )}

            {/* Only when the score did NOT come from a held state; otherwise the note above already
                explains why the model is not the author, and saying both is noise. */}
            {clarity?.aiAvailable === false && !sourceNoteKey && (
              <p className="text-xs text-muted-foreground">{t('businessRoles.aiUnavailable')}</p>
            )}

            <div>
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-sm font-medium text-foreground">{t('businessRoles.clarityOverall')}</span>
                <span className={`text-lg font-bold ${tone(overall).text}`} data-testid={`${testIdPrefix}-overall`}>
                  {overall}%
                </span>
              </div>
              <div
                className={`h-2.5 w-full rounded-full ${tone(overall).track}`}
                role="progressbar"
                aria-valuenow={overall}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div className={`h-2.5 rounded-full ${tone(overall).bar}`} style={{ width: `${overall}%` }} />
              </div>

              {/* The score is recalculated on every save, so "when" is what tells a reader whether
                  the number still describes the record. It sits with the number it qualifies rather
                  than in the header, and ends on the same edge so the two read as one unit. */}
              {updatedOn && (
                <p
                  className="mt-1.5 text-end text-[11px] text-muted-foreground"
                  data-testid={`${testIdPrefix}-updated-on`}
                >
                  {t('clarity.lastScored', { date: appDateTime(parseApiUtc(updatedOn)) })}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {overall != null && hasDetail && (
        <Accordion type="single" collapsible>
          {/* border-b-0 + border-t: a single item at the foot of a card would otherwise draw a rule
              just above the card's own bottom edge. */}
          <AccordionItem value="breakdown" className="border-b-0 border-t border-border">
            <AccordionTrigger
              className="px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              data-testid={`${testIdPrefix}-breakdown-toggle`}
            >
              {t('clarity.breakdown')}
            </AccordionTrigger>
            <AccordionContent className="px-4">
              <div className="space-y-4">
                {perField.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`${testIdPrefix}-fields`}>
                    {perField.map((f) => (
                      <div key={f.field}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-foreground">
                            {t(`${fieldLabelKey}.${f.field}`, { defaultValue: humanizeFieldLabel(f.field) })}
                          </span>
                          <span className={`text-xs font-semibold ${tone(f.percent).text}`}>{f.percent}%</span>
                        </div>
                        <div className={`h-1.5 w-full rounded-full ${tone(f.percent).track}`}>
                          <div className={`h-1.5 rounded-full ${tone(f.percent).bar}`} style={{ width: `${f.percent}%` }} />
                        </div>
                        {f.note && <p className="mt-1 text-[11px] text-muted-foreground">{f.note}</p>}
                      </div>
                    ))}
                  </div>
                )}

                {recommendations.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-foreground mb-1">{t('businessRoles.clarityRecommendations')}</p>
                    <ul className="space-y-1" data-testid={`${testIdPrefix}-recommendations`}>
                      {recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                          <span className="mt-0.5 text-warning">{'•'}</span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </section>
  );
};

export default ClarityMeter;
